import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { resolveEnrichmentFailureMessage } from '../../features/leads/utils/enrichmentError';
import {
  type EnrichmentStageReport,
  type EnrichmentStatusEvent,
  useEnrichmentStream,
} from '../../hooks/useEnrichmentStream';
import { useTriggerWorkflow } from '../../hooks/useTriggerWorkflow';
import type {
  AttachCandidate,
  SocialMedia,
  TriggerEnrichmentPayload,
} from '../../lib/workflowsApi';
import { workflowsAPI } from '../../lib/workflowsApi';
import { colors } from '../../theme/colors';
import { Box } from '../primitives/Box';
import { Input } from '../primitives/Input';
import { VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';
import { EnrichmentStageBreakdown } from './EnrichmentStageBreakdown';
import { InstagramDiscoveryPanel } from './InstagramDiscoveryPanel';
import { isValidProfileUrl } from './profileUrlValidation';
import { SocialMediaSelect } from './SocialMediaSelect';
import { resolveApiErrorMessage, useInstagramDiscovery } from './useInstagramDiscovery';
import { usePlatformSelection } from './usePlatformSelection';

type EnrichmentPhase =
  | 'idle'
  | 'checking'
  | 'exists'
  // The profile matches no lead by URL, but some look related. Prospecting used
  // to resolve this silently after the scrape by comparing company names, which
  // made "Cuculi" and "cuculimeet" two unrelated leads. The user decides now.
  | 'choose'
  | 'submitted'
  | 'scraping'
  | 'scraping_comments'
  | 'analyzing'
  | 'persisting'
  | 'completed'
  | 'partial'
  | 'insufficient'
  | 'failed';

interface WorkflowFormProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // Required: the caller must state the platform explicitly. The form locks the
  // selector only when this is 'LinkedIn' (FLOSEAI-107); there is no implicit
  // default, so omitting it can never silently lock the form.
  socialMedia: SocialMedia;
}

const TERMINAL_PHASE_BY_STATUS = {
  completed: 'completed',
  partial_success: 'partial',
  insufficient_data: 'insufficient',
} as const satisfies Partial<Record<EnrichmentStatusEvent['status'], EnrichmentPhase>>;

const LINKEDIN_STEPS: EnrichmentStatusEvent['status'][] = ['scraping', 'analyzing', 'persisting'];
const INSTAGRAM_STEPS: EnrichmentStatusEvent['status'][] = [
  'scraping',
  'scraping_comments',
  'analyzing',
  'persisting',
];

function getStepIndex(
  phase: EnrichmentPhase,
  stepOrder: EnrichmentStatusEvent['status'][],
): number {
  if (phase === 'idle') return -1;
  if (phase === 'submitted') return 0;
  if (phase === 'completed') return stepOrder.length;
  if (phase === 'failed') return -1;
  return stepOrder.indexOf(phase as EnrichmentStatusEvent['status']);
}

function ProgressSteps({
  phase,
  t,
  isInstagram,
}: Readonly<{ phase: EnrichmentPhase; t: (key: string) => string; isInstagram?: boolean }>) {
  const stepOrder = isInstagram ? INSTAGRAM_STEPS : LINKEDIN_STEPS;
  const currentIndex = getStepIndex(phase, stepOrder);

  const steps = stepOrder.map((key) => ({
    key,
    label: t(`workflow.progress.${key}`),
  }));

  return (
    <VStack spacing="md">
      {steps.map((step, index) => {
        const isActive = phase === step.key || (phase === 'submitted' && index === 0);
        const isDone = currentIndex > index || phase === 'completed';
        const isWaiting = !isActive && !isDone;
        const stepBorderColor = (() => {
          if (isDone) return colors.status.success.main;
          if (isActive) return colors.primary[500];
          return colors.border.subtle;
        })();

        return (
          <Box
            key={step.key}
            flexDirection="row"
            alignItems="center"
            gap="md"
            padding="md"
            borderRadius="md"
            backgroundColor={isActive ? colors.primary.muted : 'transparent'}
            borderWidth={1}
            borderColor={stepBorderColor}
          >
            <Box
              alignItems="center"
              justifyContent="center"
              style={[
                progressStyles.stepCircle,
                isDone && progressStyles.stepDone,
                isActive && progressStyles.stepActive,
              ]}
            >
              {isActive && <ActivityIndicator size="small" color={colors.primary[500]} />}
              {!isActive && isDone && (
                <Text variant="caption" color={colors.status.success.main} weight="bold">
                  {'\u2713'}
                </Text>
              )}
              {!isActive && !isDone && (
                <Text variant="caption" color={colors.text.tertiary} weight="semibold">
                  {index + 1}
                </Text>
              )}
            </Box>
            <Text
              variant="body"
              color={isWaiting ? colors.text.tertiary : colors.text.primary}
              weight={isActive ? 'semibold' : 'normal'}
            >
              {step.label}
            </Text>
          </Box>
        );
      })}
    </VStack>
  );
}

const progressStyles = StyleSheet.create({
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: colors.border.subtle,
  },
  stepDone: {
    borderColor: colors.status.success.main,
    backgroundColor: colors.status.success.bg,
  },
  stepActive: {
    borderColor: colors.primary[500],
  },
});

export type InstagramWorkflowMode = 'profile' | 'discover';

/**
 * The two ways into the Instagram workflow (FLOSEAI-635): enrich an account you
 * already have a URL for, or discover accounts you don't. Rendered as a segmented
 * control rather than two menu entries, because it is one workflow either way.
 */
function InstagramModeToggle({
  mode,
  onSelect,
  t,
  disabled,
}: Readonly<{
  mode: InstagramWorkflowMode;
  onSelect: (mode: InstagramWorkflowMode) => void;
  t: (key: string) => string;
  disabled?: boolean;
}>) {
  const options: Array<{ value: InstagramWorkflowMode; labelKey: string }> = [
    { value: 'profile', labelKey: 'workflow.mode.profile' },
    { value: 'discover', labelKey: 'workflow.mode.discover' },
  ];

  return (
    <Box
      flexDirection="row"
      gap="xs"
      padding="xxs"
      borderRadius="md"
      backgroundColor={colors.background.secondary}
    >
      {options.map((option) => {
        const active = mode === option.value;
        return (
          <Box
            key={option.value}
            flex={1}
            pressable={!disabled}
            onPress={disabled ? undefined : () => onSelect(option.value)}
            alignItems="center"
            paddingY="sm"
            borderRadius="sm"
            backgroundColor={active ? colors.primary[500] : 'transparent'}
            accessibilityRole="button"
            accessibilityState={{ selected: active, disabled: Boolean(disabled) }}
            accessibilityLabel={t(option.labelKey)}
          >
            <Text
              variant="bodySmall"
              color={active ? colors.primary[900] : colors.text.secondary}
              weight={active ? 'semibold' : 'normal'}
            >
              {t(option.labelKey)}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
}

export const WorkflowForm = ({
  visible,
  onClose,
  onSuccess,
  socialMedia: socialMediaProp,
}: WorkflowFormProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { currentOrganization, getAccessToken } = useAuth();
  const { showWarning } = useToast();
  const [name, setName] = useState('');
  // FLOSEAI-107: the LinkedIn enrichment entry point pre-selects and LOCKS the
  // platform; other entry points (e.g. Instagram) keep the field selectable.
  // `socialMedia` is what the payload carries — always the prop when locked.
  const { socialMedia, isPlatformLocked, selectPlatform } = usePlatformSelection(socialMediaProp);
  const [profileUrl, setProfileUrl] = useState('');
  // Which half of the Instagram workflow the user is in (FLOSEAI-635). LinkedIn
  // has no discovery counterpart here, so the toggle only renders for Instagram.
  const [mode, setMode] = useState<InstagramWorkflowMode>('profile');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<EnrichmentPhase>('idle');
  const [existingLeadId, setExistingLeadId] = useState<string | null>(null);
  const [attachCandidates, setAttachCandidates] = useState<AttachCandidate[]>([]);
  const [resultData, setResultData] = useState<{
    leadId?: string;
    name?: string;
    score?: number;
    error?: string;
    signalGap?: EnrichmentStatusEvent['signalGap'];
    stages?: EnrichmentStageReport[];
  }>({});

  const mutation = useTriggerWorkflow();

  const handleSseEvent = useCallback(
    (event: EnrichmentStatusEvent) => {
      if (profileUrl && event.profileUrl && event.profileUrl !== profileUrl.trim()) return;

      if (
        event.status === 'completed' ||
        event.status === 'partial_success' ||
        event.status === 'insufficient_data'
      ) {
        if (event.skipped) {
          setExistingLeadId(event.leadId ?? null);
          setPhase('exists');
        } else {
          setPhase(TERMINAL_PHASE_BY_STATUS[event.status]);
          setResultData({
            leadId: event.leadId,
            name: event.name,
            score: event.score,
            signalGap: event.signalGap,
            stages: event.stages,
          });
        }
      } else if (event.status === 'failed' || event.status === 'auth_failed') {
        setPhase('failed');
        setResultData({
          error: resolveEnrichmentFailureMessage(event, t),
          stages: event.stages,
        });
      } else {
        setPhase(event.status);
      }
    },
    [t, profileUrl],
  );

  useEnrichmentStream({
    organizationId: currentOrganization?.id ?? '',
    enabled:
      phase === 'submitted' ||
      phase === 'scraping' ||
      phase === 'scraping_comments' ||
      phase === 'analyzing' ||
      phase === 'persisting',
    onEvent: handleSseEvent,
    getAccessToken,
  });

  const isInstagram = socialMedia === 'Instagram';
  const discovery = useInstagramDiscovery(currentOrganization?.id);
  const discoveryBusy = discovery.phase === 'searching' || discovery.phase === 'importing';

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('workflow.nameRequired');
    }

    if (!profileUrl.trim()) {
      newErrors.profileUrl = t('workflow.profileUrlRequired');
    } else if (!isValidProfileUrl(profileUrl, socialMedia)) {
      newErrors.profileUrl = isInstagram
        ? t('workflow.profileUrlInvalidInstagram')
        : t('workflow.profileUrlInvalidLinkedIn');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Runs the enrichment with the decision the user made: attached to a lead they
   * picked, or explicitly a new one — which also tells the intake not to fold it
   * into a company-name match behind their back.
   */
  const submitEnrichment = async (decision: { leadId?: string; forceNewLead?: boolean }) => {
    if (!currentOrganization?.id) return;

    const payload: TriggerEnrichmentPayload = {
      organizationId: currentOrganization.id,
      profileUrl: profileUrl.trim(),
      socialMedia,
      name: name.trim(),
      ...decision,
    };

    setPhase('submitted');

    try {
      await mutation.mutateAsync(payload);
    } catch {
      setPhase('idle');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!currentOrganization?.id) return;

    setPhase('checking');

    try {
      const { data } = await workflowsAPI.findLeadBySourceUrl(
        currentOrganization.id,
        profileUrl.trim(),
      );
      if (data.leadId) {
        setExistingLeadId(data.leadId);
        setPhase('exists');
        return;
      }
      if (data.assignedToOther) {
        setExistingLeadId(null);
        setPhase('exists');
        return;
      }

      const { data: candidates } = await workflowsAPI.attachCandidates(
        currentOrganization.id,
        profileUrl.trim(),
        name.trim(),
      );
      if (candidates.length > 0) {
        setAttachCandidates(candidates);
        setPhase('choose');
        return;
      }
    } catch {
      // If check fails, proceed with enrichment anyway
    }

    const payload: TriggerEnrichmentPayload = {
      organizationId: currentOrganization.id,
      profileUrl: profileUrl.trim(),
      socialMedia,
      name: name.trim(),
    };

    setPhase('submitted');

    try {
      await mutation.mutateAsync(payload);
    } catch (error) {
      const status =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response
          ? (error.response as { status: number }).status
          : undefined;

      if (status === 409) {
        setPhase('idle');
        // Clear any stale validation error from a prior submission so the
        // 409 warning toast isn't paired with an outdated form-level error.
        setErrors({});
        showWarning(t('workflow.card.workflowPaused'));
        return;
      }

      if (status === 403) {
        setPhase('idle');
        setErrors({ form: resolveApiErrorMessage(error) });
        return;
      }

      console.error('Failed to trigger enrichment:', error);
      setPhase('idle');
      setErrors({ form: t('workflow.errorGeneric') });
    }
  };

  const handleViewExistingLead = () => {
    if (!existingLeadId) return;
    resetForm();
    onClose();
    router.push(`/lead/${existingLeadId}`);
  };

  const handleClose = () => {
    const wasSuccessful = phase === 'completed' || phase === 'partial' || phase === 'insufficient';
    resetForm();
    resetDiscoveryUnlessBusy();
    if (wasSuccessful) {
      onSuccess?.();
    }
    onClose();
  };

  const resetDiscoveryUnlessBusy = () => {
    if (!discoveryBusy) discovery.reset();
  };

  const selectMode = (next: InstagramWorkflowMode) => {
    if (next === mode) return;
    resetDiscoveryUnlessBusy();
    setMode(next);
  };

  const resetForm = () => {
    setName('');
    selectPlatform(socialMediaProp);
    setProfileUrl('');
    setErrors({});
    setPhase('idle');
    setExistingLeadId(null);
    setResultData({});
    mutation.reset();
  };

  const handleRetry = () => {
    setPhase('idle');
    setResultData({});
    setErrors({});
    mutation.reset();
  };

  const handleNewRun = () => {
    resetForm();
  };

  const isProcessing =
    phase === 'checking' ||
    phase === 'submitted' ||
    phase === 'scraping' ||
    phase === 'scraping_comments' ||
    phase === 'analyzing' ||
    phase === 'persisting';

  const isDiscovering = isInstagram && mode === 'discover';

  const renderContent = () => {
    // FLOSEAI-635 — discovery is the other half of the SAME Instagram workflow,
    // not a separate screen: one entry point, two ways in. Enrich a profile you
    // already know, or go find accounts you don't.
    if (isDiscovering) {
      return (
        <VStack spacing="xl">
          <InstagramModeToggle mode={mode} onSelect={selectMode} t={t} disabled={discoveryBusy} />
          <InstagramDiscoveryPanel
            discovery={discovery}
            onDone={() => {
              discovery.reset();
              onSuccess?.();
              handleClose();
            }}
          />
        </VStack>
      );
    }

    if (phase === 'choose') {
      return (
        <VStack spacing="lg">
          <Box backgroundColor={colors.background.secondary} borderRadius="md" padding="lg">
            <Text variant="h6" color={colors.text.primary} weight="bold">
              {t('workflow.attachLead.title')}
            </Text>
            <Text variant="bodySmall" color={colors.text.secondary} style={{ marginTop: 8 }}>
              {t('workflow.attachLead.description')}
            </Text>
          </Box>

          {attachCandidates.map((candidate) => (
            <Box
              key={candidate.leadId}
              pressable
              onPress={() => submitEnrichment({ leadId: candidate.leadId })}
              backgroundColor={colors.background.elevated}
              borderRadius="md"
              padding="md"
              borderWidth={1}
              borderColor={colors.border.muted}
              accessibilityRole="button"
              accessibilityLabel={`${t('workflow.attachLead.attachTo')}: ${
                candidate.fullName || candidate.company || candidate.leadId
              }`}
            >
              <Text variant="body" color={colors.text.primary} weight="semibold">
                {candidate.fullName || candidate.company || candidate.leadId}
              </Text>
              <Text variant="caption" color={colors.text.tertiary} style={{ marginTop: 4 }}>
                {[
                  candidate.company,
                  candidate.platforms.map((p) => t(`common.sources.${p}`)).join(' · '),
                  candidate.alreadyEnriched ? t('workflow.attachLead.alreadyHasProfile') : null,
                ]
                  .filter(Boolean)
                  .join(' — ')}
              </Text>
            </Box>
          ))}

          <Box
            pressable
            onPress={() => submitEnrichment({ forceNewLead: true })}
            backgroundColor={colors.primary[500]}
            borderRadius="md"
            padding="md"
            alignItems="center"
            accessibilityRole="button"
            accessibilityLabel={t('workflow.attachLead.createNew')}
          >
            <Text variant="body" color={colors.primary[900]} weight="semibold">
              {t('workflow.attachLead.createNew')}
            </Text>
          </Box>
        </VStack>
      );
    }

    if (phase === 'exists') {
      return (
        <VStack spacing="lg">
          <Box
            backgroundColor={colors.status.warning.bg}
            borderRadius="md"
            padding="lg"
            borderWidth={1}
            borderColor={colors.status.warning.main}
          >
            <Text variant="h6" color={colors.status.warning.dark} align="center" weight="bold">
              {t('workflow.leadExists.title')}
            </Text>
            <Text
              variant="bodySmall"
              color={colors.text.secondary}
              align="center"
              style={{ marginTop: 8 }}
            >
              {t('workflow.leadExists.description')}
            </Text>
          </Box>

          <Box
            pressable
            onPress={handleViewExistingLead}
            backgroundColor={colors.primary[500]}
            borderRadius="md"
            padding="md"
            alignItems="center"
            accessibilityLabel={t('workflow.leadExists.viewLead')}
            accessibilityRole="button"
          >
            <Text variant="body" color={colors.primary[900]} weight="semibold">
              {t('workflow.leadExists.viewLead')}
            </Text>
          </Box>

          <Box
            pressable
            onPress={handleNewRun}
            borderRadius="md"
            padding="md"
            alignItems="center"
            borderWidth={1}
            borderColor={colors.border.default}
            accessibilityLabel={t('workflow.leadExists.newSearch')}
            accessibilityRole="button"
          >
            <Text variant="body" color={colors.text.secondary} weight="semibold">
              {t('workflow.leadExists.newSearch')}
            </Text>
          </Box>
        </VStack>
      );
    }

    if (phase === 'completed') {
      return (
        <VStack spacing="lg">
          <Box
            backgroundColor={colors.status.success.bg}
            borderRadius="md"
            padding="lg"
            borderWidth={1}
            borderColor={colors.status.success.main}
          >
            <Text variant="h6" color={colors.status.success.main} align="center" weight="bold">
              {t('workflow.progress.completed')}
            </Text>
            {resultData.name && (
              <Box flexDirection="row" justifyContent="center" style={{ marginTop: 12 }}>
                <Text variant="body" color={colors.status.success.dark} weight="semibold">
                  {resultData.name}
                </Text>
              </Box>
            )}
            {resultData.score !== undefined && (
              <Box flexDirection="row" justifyContent="center" style={{ marginTop: 8 }}>
                <Text variant="bodySmall" color={colors.text.secondary}>
                  {t('workflow.progress.score')}:{' '}
                </Text>
                <Text variant="bodySmall" color={colors.status.success.main} weight="bold">
                  {resultData.score}
                </Text>
              </Box>
            )}
          </Box>

          {resultData.stages?.some((stage) => stage.status === 'failed') ? (
            <EnrichmentStageBreakdown stages={resultData.stages} t={t} />
          ) : (
            <ProgressSteps phase={phase} t={t} isInstagram={isInstagram} />
          )}

          <Box
            pressable
            onPress={handleNewRun}
            backgroundColor={colors.primary[500]}
            borderRadius="md"
            padding="md"
            alignItems="center"
            accessibilityLabel={t('workflow.progress.newRun')}
            accessibilityRole="button"
          >
            <Text variant="body" color={colors.primary[900]} weight="semibold">
              {t('workflow.progress.newRun')}
            </Text>
          </Box>
        </VStack>
      );
    }

    if (phase === 'partial' || phase === 'insufficient') {
      const isPartial = phase === 'partial';
      const tone = isPartial ? colors.status.warning : colors.status.info;
      return (
        <VStack spacing="lg">
          <Box
            backgroundColor={tone.bg}
            borderRadius="md"
            padding="lg"
            borderWidth={1}
            borderColor={tone.main}
          >
            <Text variant="h6" color={tone.main} align="center" weight="bold">
              {t(
                isPartial
                  ? 'workflow.progress.partialSuccess'
                  : 'workflow.progress.insufficientData',
              )}
            </Text>
            {resultData.name && (
              <Box flexDirection="row" justifyContent="center" style={{ marginTop: 12 }}>
                <Text variant="body" color={colors.text.primary} weight="semibold">
                  {resultData.name}
                </Text>
              </Box>
            )}
            {!isPartial && resultData.signalGap && (
              <Text
                variant="bodySmall"
                color={colors.text.secondary}
                align="center"
                style={{ marginTop: 8 }}
              >
                {t(`leads.enrichment.signalGap.${resultData.signalGap}`)}
              </Text>
            )}
            {isPartial && resultData.score !== undefined && (
              <Box flexDirection="row" justifyContent="center" style={{ marginTop: 8 }}>
                <Text variant="bodySmall" color={colors.text.secondary}>
                  {t('workflow.progress.score')}:{' '}
                </Text>
                <Text variant="bodySmall" color={tone.main} weight="bold">
                  {resultData.score}
                </Text>
              </Box>
            )}
          </Box>

          <EnrichmentStageBreakdown stages={resultData.stages} t={t} />

          <Box
            pressable
            onPress={handleNewRun}
            backgroundColor={colors.primary[500]}
            borderRadius="md"
            padding="md"
            alignItems="center"
            accessibilityLabel={t('workflow.progress.newRun')}
            accessibilityRole="button"
          >
            <Text variant="body" color={colors.primary[900]} weight="semibold">
              {t('workflow.progress.newRun')}
            </Text>
          </Box>
        </VStack>
      );
    }

    if (phase === 'failed') {
      return (
        <VStack spacing="lg">
          <Box
            backgroundColor={colors.status.error.bg}
            borderRadius="md"
            padding="lg"
            borderWidth={1}
            borderColor={colors.status.error.main}
          >
            <Text variant="h6" color={colors.status.error.main} align="center" weight="bold">
              {t('workflow.progress.failed')}
            </Text>
            {resultData.error && (
              <Text
                variant="bodySmall"
                color={colors.text.secondary}
                align="center"
                style={{ marginTop: 8 }}
              >
                {resultData.error}
              </Text>
            )}
          </Box>

          <EnrichmentStageBreakdown stages={resultData.stages} t={t} />

          <Box
            pressable
            onPress={handleRetry}
            backgroundColor={colors.primary[500]}
            borderRadius="md"
            padding="md"
            alignItems="center"
            accessibilityLabel={t('workflow.progress.retry')}
            accessibilityRole="button"
          >
            <Text variant="body" color={colors.primary[900]} weight="semibold">
              {t('workflow.progress.retry')}
            </Text>
          </Box>
        </VStack>
      );
    }

    if (isProcessing) {
      return (
        <VStack spacing="lg">
          <Text variant="h6" color={colors.text.primary} align="center" weight="bold">
            {t('workflow.progress.title')}
          </Text>
          <ProgressSteps phase={phase} t={t} isInstagram={isInstagram} />
        </VStack>
      );
    }

    return (
      <>
        <Text variant="body" color={colors.text.secondary}>
          {t('workflow.description')}
        </Text>

        {Boolean(errors.form) && (
          <Box
            backgroundColor={colors.status.error.bg}
            borderRadius="md"
            padding="md"
            borderWidth={1}
            borderColor={colors.status.error.main}
          >
            <Text variant="body" color={colors.status.error.main}>
              {errors.form}
            </Text>
          </Box>
        )}

        {isInstagram && (
          <InstagramModeToggle mode={mode} onSelect={selectMode} t={t} disabled={isProcessing} />
        )}

        <SocialMediaSelect
          value={socialMedia}
          onSelect={selectPlatform}
          locked={isPlatformLocked}
        />

        <Input
          label={t('workflow.nameLabel')}
          value={name}
          onChangeText={setName}
          placeholder={t('workflow.namePlaceholder')}
          error={errors.name}
          autoCapitalize="words"
        />

        <Input
          label={t('workflow.profileUrlLabel')}
          value={profileUrl}
          onChangeText={setProfileUrl}
          placeholder={t(
            isInstagram
              ? 'workflow.profileUrlPlaceholderInstagram'
              : 'workflow.profileUrlPlaceholder',
          )}
          error={errors.profileUrl}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="url"
        />
      </>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <Box flex={1} backgroundColor={colors.background.primary}>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            paddingX="xl"
            paddingY="lg"
            borderBottomWidth={1}
            borderBottomColor={colors.border.subtle}
          >
            <Text variant="h6" color={colors.text.primary}>
              {t(isDiscovering ? 'workflow.discovery.title' : 'workflow.title')}
            </Text>
            <Box
              onPress={handleClose}
              pressable
              hitSlop={10}
              accessibilityLabel={t('workflow.closeButton')}
            >
              <Text variant="body" color={colors.primary[500]}>
                {t('workflow.closeButton')}
              </Text>
            </Box>
          </Box>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
            <VStack spacing="xl">{renderContent()}</VStack>
          </ScrollView>

          {phase === 'idle' && !isDiscovering && (
            <Box
              paddingX="xl"
              paddingY="lg"
              borderTopWidth={1}
              borderTopColor={colors.border.subtle}
            >
              <Box
                pressable
                onPress={handleSubmit}
                disabled={mutation.isPending}
                backgroundColor={mutation.isPending ? colors.primary[700] : colors.primary[500]}
                borderRadius="md"
                padding="md"
                alignItems="center"
                minHeight={48}
                justifyContent="center"
                accessibilityLabel={
                  mutation.isPending ? t('workflow.submittingButton') : t('workflow.submitButton')
                }
                accessibilityRole="button"
                accessibilityState={{ disabled: mutation.isPending }}
              >
                {mutation.isPending ? (
                  <Box flexDirection="row" alignItems="center" gap="sm">
                    <ActivityIndicator size="small" color={colors.text.primary} />
                    <Text variant="body" color={colors.text.primary} weight="semibold">
                      {t('workflow.submittingButton')}
                    </Text>
                  </Box>
                ) : (
                  <Text variant="body" color={colors.primary[900]} weight="semibold">
                    {t('workflow.submitButton')}
                  </Text>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </KeyboardAvoidingView>
    </Modal>
  );
};
